import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { PermissionsAndroid, Dimensions } from "react-native";
import DocumentPicker from "react-native-document-picker";

import {
  launchCamera,
  launchImageLibrary,
  MediaType,
} from "react-native-image-picker";
import { Platform } from "react-native";
import { createThumbnail } from "react-native-create-thumbnail";
import {
  addFileToUpload,
  updateFilesToUpload,
} from "@microdoing/shared/services/upload/actions";
import { getFilesToUpload } from "@microdoing/shared/services/upload/selectors";
import { IFileToUploadType } from "@microdoing/shared/services/upload/types";
import getFileExtension from "@microdoing/shared/utils/getFileExtension";
import hasVideoExtension from "@microdoing/shared/utils/hasVideoExtension";
import askPermissionAgain from "app/utils/askPermissionAgain";
import i18n from "@microdoing/shared/i18n";
import { isWeb } from "@microdoing/shared/utils/deviceDetectors";

const { height, width } = Dimensions.get("window");

const MAX_HEIGHT = height * 2;
const MAX_WIDTH = width * 2;

const videoOptions = {
  saveToPhotos: true,
  mediaType: "video",
  videoQuality: Platform.OS === "ios" ? "medium" : "low",
};

const photoOptions = {
  saveToPhotos: true,
  mediaType: "photo",
  includeBase64: false,
  quality: 1,
};

const galleryOptions = {
  selectionLimit: 0,
  mediaType: "mixed",
};

const getResolvedPath = async uri => {
  let correctPath = uri;
  return correctPath;
};

export const requestStoragePermission = async () => {
  if (Platform.OS !== "android") return true;

  const pm1 = await PermissionsAndroid.check(
    PermissionsAndroid.PERMISSIONS.READ_EXTERNAL_STORAGE,
  );
  const pm2 = await PermissionsAndroid.check(
    PermissionsAndroid.PERMISSIONS.WRITE_EXTERNAL_STORAGE,
  );

  if (pm1 && pm2) return true;

  const userResponse = await PermissionsAndroid.requestMultiple([
    PermissionsAndroid.PERMISSIONS.READ_EXTERNAL_STORAGE,
    PermissionsAndroid.PERMISSIONS.WRITE_EXTERNAL_STORAGE,
  ]);

  if (
    userResponse["android.permission.READ_EXTERNAL_STORAGE"] === "granted" &&
    userResponse["android.permission.WRITE_EXTERNAL_STORAGE"] === "granted"
  ) {
    return true;
  } else {
    return false;
  }
};

interface IUserLaunchCamera {
  hasVideoOption?: boolean;
  hasPhotoOption?: boolean;
}

export const useLaunchCamera = ({
  hasVideoOption = true,
  hasPhotoOption = true,
}: IUserLaunchCamera) => {
  const [error, setError] = useState("");
  const filesToUpload: IFileToUploadType[] = useSelector(getFilesToUpload);

  const dispatch = useDispatch();

  const requestCameraPermission = async () => {
    console.log("requesting permission Android");
    try {
      const cameraGranted = await PermissionsAndroid.request(
        PermissionsAndroid.PERMISSIONS.CAMERA,
      );
      const storageGranted = await PermissionsAndroid.request(
        PermissionsAndroid.PERMISSIONS.WRITE_EXTERNAL_STORAGE,
      );
      if (
        cameraGranted === PermissionsAndroid.RESULTS.GRANTED &&
        storageGranted === PermissionsAndroid.RESULTS.GRANTED
      ) {
        console.log("Camera permission given");
      } else {
        console.log("Camera permission denied");
        return setError("No permission granted");
      }
    } catch (err) {
      console.log(err);
      setError(err);
    }
  };

  const onLaunchCamera = (optionType = "gallery") => (selectionLimit = 0) => {
    let mediaType: MediaType =
      hasVideoOption && hasPhotoOption
        ? "mixed"
        : hasVideoOption
        ? "video"
        : "photo";

    let options = {};
    if (optionType === "video") {
      options = videoOptions;
    } else if (optionType === "photo") {
      options = photoOptions;
    } else if (optionType === "gallery") {
      options = { ...galleryOptions, mediaType, selectionLimit };

      return launchImageLibrary(
        {
          ...options,
          maxHeight: MAX_HEIGHT,
          maxWidth: MAX_WIDTH,
          mediaType,
        },
        async ({ assets, didCancel, errorCode, errorMessage }) => {
          if (errorMessage) {
            setError(errorMessage);
            return askPermissionAgain({
              alertMessage: i18n.t("alert-ask-permission-message-general"),
            });
          }
          if (!assets?.length) return;

          const formattedAssetsPromises = assets.map(async asset => {
            if (isWeb) {
              const { uri = "" } = asset || { uri: "" };
              const fileType = uri.match(/[^:]\w+\/[\w-+\d.]+(?=;|,)/)?.[0];
              const fileExtension = fileType?.split("/")?.[1];
              const fileName = `super.${fileExtension}`;

              return {
                ...asset,
                fileName,
                fileType,
              };
            }
            if (asset.duration) {
              const thumbnail = await createThumbnail({
                url: asset?.uri || "",
              });

              const { uri = "" } = asset;

              let fileName = getFileName(uri);
              let extension = getFileExtension(fileName);
              let isVideoExtension = hasVideoExtension(fileName);

              // Bug from Android upload gallery - we can't detect the extension of video
              if (Platform.OS === "android" && !isVideoExtension) {
                extension = "mp4";
                fileName = `${fileName}.${extension}`;
              }

              return {
                ...asset,
                preview: thumbnail?.path,
                fileName,
                fileType: `video/${extension}`,
              };
            }
            return asset;
          });

          const formattedAssets = await Promise.all(formattedAssetsPromises);
          const goodAssets = [...filesToUpload, ...formattedAssets];
          dispatch(updateFilesToUpload(goodAssets));
        },
      );
    }

    launchCamera(
      {
        ...options,
        maxHeight: MAX_HEIGHT,
        maxWidth: MAX_WIDTH,
        mediaType: optionType === "video" ? "video" : "photo",
      },
      async ({ assets, didCancel, errorCode, errorMessage }) => {
        if (errorMessage || errorCode) {
          setError(errorMessage || errorCode || "");
          return askPermissionAgain({
            alertMessage: i18n.t("alert-ask-permission-message-general"),
          });
        }
        if (!assets?.length) return;
        const asset = assets[0];

        const { url, type, uri } = asset;
        if (asset.duration) {
          const thumbnail = await createThumbnail({
            url: asset?.uri || "",
          });

          const fileName = getFileName(uri);
          const extension = getFileExtension(fileName);

          return dispatch(
            addFileToUpload({
              ...asset,
              preview: thumbnail.path,
              fileType: `video/${extension}`,
              fileName,
            }),
          );
        }

        dispatch(addFileToUpload(asset));
      },
    );
  };

  const openDocumentPicker = async () => {
    try {
      await requestStoragePermission();
      const results = await DocumentPicker.pickMultiple({
        allowMultiSelection: true,
        copyTo: "cachesDirectory",
        // type: [DocumentPicker.types.images],
      });
      for (const res of results) {
        const { uri, type, name, size, fileCopyUri } = res;
        const goodUri = Platform.OS === "ios" ? uri : fileCopyUri;

        let preview = "";
        if (hasVideoExtension(name)) {
          const thumbnail = await createThumbnail({
            url: uri || "",
          });
          preview = thumbnail?.path;
        }

        const realPath =
          Platform.OS === "ios"
            ? await getResolvedPath(goodUri)
            : `file://${fileCopyUri}`;

        dispatch(
          updateFilesToUpload([
            ...filesToUpload,
            {
              ...res,
              fileType: type,
              fileName: name,
              preview,
              uri: realPath,
            },
          ]),
        );
      }
    } catch (err) {
      if (DocumentPicker.isCancel(err)) {
        // User cancelled the picker, exit any dialogs or menus and move on
      } else {
        console.log(err);
        return askPermissionAgain({
          alertMessage: i18n.t("alert-ask-permission-message-general"),
        });
      }
    }
  };

  //  "photo" | "video" | "gallery" | "document" = "gallery"
  const openCamera = async (optionType: any, selectionLimit = 0) => {
    setError("");
    if (Platform.OS === "android" && optionType !== "gallery") {
      await requestCameraPermission();
    }

    onLaunchCamera(optionType)(selectionLimit);
  };

  const openNativeModule = (
    optionType: any,
    selectionLimit = 0,
  ) => async () => {
    if (optionType === "document") return openDocumentPicker();
    return openCamera(optionType, selectionLimit);
  };

  return [error, setError, openNativeModule as any];
};

const getFileName = uri => {
  const fileName = uri.substring(uri.lastIndexOf("/") + 1);
  return fileName;
};
